import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, BarChart3 } from 'lucide-react';
import { obterDashboardDeContratos } from '@/features/contratos/contratos.api';
import { cn } from '@/lib/cn';

function fmtMRR(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

function KpiCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'ok' | 'warn' | 'danger' | 'neutral';
}) {
  const cls = {
    ok: 'border-green-500/20 bg-green-500/5 text-green-600',
    warn: 'border-amber-500/20 bg-amber-500/5 text-amber-600',
    danger: 'border-destructive/20 bg-destructive/5 text-destructive',
    neutral: 'border-border bg-surface text-foreground',
  }[tone ?? 'neutral'];
  return (
    <div className={cn('rounded-xl border p-5 space-y-1', cls)}>
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function ContratosDashboardPage() {
  const { data, isPending } = useQuery({
    queryKey: ['contratos', 'dashboard'],
    queryFn: obterDashboardDeContratos,
    staleTime: 60_000,
  });

  if (isPending)
    return <div className="py-12 text-center text-sm text-muted-foreground">Carregando…</div>;
  if (!data) return null;

  const totalExpirando = data.expirandoEm30 + data.expirandoEm60 + data.expirandoEm90;

  return (
    <div className="mx-auto max-w-[1000px] space-y-6">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Dashboard de Contratos</h1>
        <Link to="/contratos" className="text-xs text-muted-foreground hover:text-foreground">
          Ver lista completa →
        </Link>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Contratos ativos" value={data.totalAtivos} tone="ok" />
        <KpiCard
          label="MRR"
          value={fmtMRR(data.mrr)}
          sub="soma dos termos ativos"
          tone="neutral"
        />
        <KpiCard
          label="Suspensos"
          value={data.totalSuspensos}
          tone={data.totalSuspensos > 0 ? 'warn' : 'neutral'}
        />
        <KpiCard label="Encerrados" value={data.totalEncerrados} tone="neutral" />
      </div>

      {/* Expiry countdown */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Vencimentos próximos</h2>
        <div className="grid grid-cols-3 gap-3">
          {(
            [
              {
                label: 'Vencem em 30 dias',
                value: data.expirandoEm30,
                tone: data.expirandoEm30 > 0 ? ('danger' as const) : ('ok' as const),
              },
              {
                label: 'Vencem em 31–60 dias',
                value: data.expirandoEm60,
                tone: data.expirandoEm60 > 0 ? ('warn' as const) : ('ok' as const),
              },
              {
                label: 'Vencem em 61–90 dias',
                value: data.expirandoEm90,
                tone: 'neutral' as const,
              },
            ] as const
          ).map(({ label, value, tone }) => (
            <div
              key={label}
              className={cn(
                'rounded-xl border p-4 text-center space-y-1',
                tone === 'danger'
                  ? 'border-destructive/20 bg-destructive/5'
                  : tone === 'warn'
                    ? 'border-amber-500/20 bg-amber-500/5'
                    : tone === 'ok' && value === 0
                      ? 'border-green-500/20 bg-green-500/5'
                      : 'border-border bg-muted/20',
              )}
            >
              <p
                className={cn(
                  'text-3xl font-bold tabular-nums',
                  tone === 'danger'
                    ? 'text-destructive'
                    : tone === 'warn'
                      ? 'text-amber-600'
                      : 'text-foreground',
                )}
              >
                {value}
              </p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
        {totalExpirando > 0 && (
          <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            {totalExpirando} contrato{totalExpirando !== 1 ? 's' : ''} venc
            {totalExpirando !== 1 ? 'em' : 'e'} nos próximos 90 dias — verifique renovações.
          </div>
        )}
      </section>

      {/* Top accounts */}
      {data.topContasPorMrr.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold">Top clientes por MRR</h2>
          <div className="rounded-xl border border-border overflow-hidden">
            {data.topContasPorMrr.map((c, i) => {
              const max = data.topContasPorMrr[0]?.mrr ?? 0;
              const pct = max > 0 ? Math.round((c.mrr / max) * 100) : 0;
              return (
                <div
                  key={c.id}
                  className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-0"
                >
                  <span className="w-5 text-center text-xs font-mono text-muted-foreground">
                    {i + 1}
                  </span>
                  <span className="flex-1 text-sm font-medium truncate">{c.name}</span>
                  <div className="w-24 h-1.5 overflow-hidden rounded-full bg-muted flex-shrink-0">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs font-semibold tabular-nums text-right w-28 flex-shrink-0">
                    {fmtMRR(c.mrr)}
                    <span className="text-muted-foreground font-normal">/mês</span>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Quick link */}
      <div className="flex gap-3">
        <Link
          to="/contratos"
          className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm hover:bg-muted transition-colors"
        >
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          Ver todos os contratos
        </Link>
      </div>
    </div>
  );
}
